/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// React library
import { Component } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import ReactQuill from 'react-quill';

// React redux
import { connect } from 'react-redux';

// Project library
import { CTBIDS_CONTENT } from 'shared/settings/config';
import Validators from 'shared/validators';

// Service
import Axios from 'axios';
import { getAdminHeaders, getAdminUrl } from 'shared/http';

// style imports
import './CtbidsContentEdit.scss';
import { webContentAdd, webContentDetail, webContentUpdate } from 'shared/settings/apiconfig';

/**
 * this page is used to add/edit ctbids content
 * 
 * @param props
 * 
 * @returns html code to render the ctbids content add/edit
 * 
**/

class CtbidsContentEdit extends Component {
    constructor(props) {
        super(props);
        this.contentId = this.props?.match?.params?.contentId;

        this.modules = {
            toolbar: [
                [{ 'font': [] }],
                [{ size: [] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'align': [] }],
                ['link'],
                ['clean']
            ],
            clipboard: {
                // toggle to add extra line breaks when pasting HTML:
                matchVisual: false,
                matchers: [
                    ['span', (node, delta) => {
                      delta.ops.forEach(op => {
                        if (op.attributes) {
                          const isLink = !!op.attributes.link;          
                          Object.keys(op.attributes).forEach(attr => {
                            if (
                              attr !== 'link' &&
                              !(attr === 'color' && isLink) 
                            ) {
                              delete op.attributes[attr];
                            }
                          });
                        }
                      });
                      return delta;
                    }],
                    ['a', (node, delta) => {
                      return delta;
                    }]
                  ]
                },
        };
        this.formats = ['background', 'bold', 'color', 'font', 'code', 'italic', 'link', 'size', 'strike', 'script', 'underline',
            'blockquote', 'header', 'indent', 'list', 'align', 'direction', 'code-block', 'formula'
            // 'image'
            // 'video'
        ];
        this.textEditorRef = null;

        this.state = {
            formControls: {
                type: { value: '', valid: false, error: '', validators: [Validators.required()] },
                description: { value: '', valid: false, error: '', validators: [Validators.required()] },
                status: { value: '', valid: false, error: '', validators: [] }
            },
            formValid: false,
            pageLoading: true,
            loading: false,
            apiError: false
        };

        this.contentType = CTBIDS_CONTENT;
        this.status = [
            { name: "Inactive", value: 0 },
            { name: "Active", value: 1 },
        ]
        this.edit = this.contentId ? true : false;
        this.typeValue = null;
        this.approveBack = false;
    }

    componentDidMount() {
        this.getData();
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', this.onBack);
    }

    onBack = (e) => {
        e.preventDefault();
        if (this.approveBack) {
            window.removeEventListener('popstate', this.onBack);
        } else {
            this.approveBack = true;
            this.goBack();
        }
    }

    // get required data
    getData = () => {
        if (this.edit) {
            Axios.get(getAdminUrl(`${webContentDetail}/${this.contentId}`), getAdminHeaders()).then(res => {
                const data = res.data.data[0];
                this.typeValue = JSON.parse(JSON.stringify(data.type));
                this.setState({
                    formControls: {
                        ...this.state.formControls,
                        type: {
                            ...this.state.formControls.type,
                            value: data.type,
                            valid: true
                        },
                        description: {
                            ...this.state.formControls.description,
                            value: data.description,
                            valid: true
                        },
                        status: {
                            ...this.state.formControls.status,
                            value: data.status,
                            valid: true
                        }
                    },
                    formValid: true,
                    pageLoading: false,
                })
            }).catch(error => {
                this.setState({
                    pageLoading: false,
                    apiError: true
                });
            });
        } else {
            this.setState({
                formControls: {
                    ...this.state.formControls,
                    status: {
                        ...this.state.formControls.status,
                        value: 0,
                        valid: true
                    }
                },
                pageLoading: false,
            });
        }
    }

    /* called when template input change */
    templateChange = (value) => {
        let eventValue = value;
        let valid = true;
        let controlError = '';

        if (value.replace(/<(.|\n)*?>/g, '').trim().length === 0) {
            valid = false;
            eventValue = "";
        }

        /* checks all the validators for the control */
        for (let validator of this.state.formControls.description.validators) {
            controlError = validator(eventValue);
            if (controlError) break;
        }

        /* sets the state of the control and form validity */
        this.setState({
            formControls: {
                ...this.state.formControls,
                description: {
                    ...this.state.formControls.description,
                    value: valid ? value : "",
                    valid: !Boolean(controlError),
                    error: controlError
                }
            }
        },
            () => {
                /* check form validity */
                if (controlError)
                    this.setState({ formValid: false });
                else {
                    let formValid = true;
                    for (let control of Object.keys(this.state.formControls)) {
                        if (!this.state.formControls[control].valid) {
                            formValid = false;
                            break;
                        }
                    }
                    this.setState({ formValid: formValid });
                }
            });
    }

    /* checks whether the input is valid or not */
    onInputValidation = (event) => {
        let controlError = '';

        /* checks all the validators for the control */
        for (let validator of this.state.formControls[event.target.name].validators) {
            controlError = validator(event.target.value);
            if (controlError) break;
        }

        /* sets the state of the control and form validity */
        this.setState({
            formControls: {
                ...this.state.formControls,
                [event.target.name]: {
                    ...this.state.formControls[event.target.name],
                    value: event.target.value,
                    valid: !Boolean(controlError),
                    error: controlError
                }
            }
        },
            () => {
                /* check form validity */
                if (controlError)
                    this.setState({ formValid: false });
                else {
                    let formValid = true;
                    for (let control of Object.keys(this.state.formControls)) {
                        if (!this.state.formControls[control].valid) {
                            formValid = false;
                            break;
                        }
                    }
                    this.setState({ formValid: formValid });
                }
            });
    }

    goBack = () => {
        this.props.history.push({ pathname: '/ctbids-content/list', state: this.props?.location?.state });
    }

    submit = () => {
        this.setState({
            loading: true
        }, () => {
            const data = {
                type: this.state.formControls.type.value,
                description: this.state.formControls.description.value
            };
            if (this.edit) {
                data["id"] = this.contentId;
                data['status'] = this.state.formControls.status.value;
                data['type'] = this.typeValue;
                Axios.put(getAdminUrl(webContentUpdate), JSON.stringify({ data }), getAdminHeaders()).then(res => {
                    this.goBack();
                }).catch(error => {
                    this.setState({
                        loading: false
                    });
                })
            } else {
                Axios.post(getAdminUrl(webContentAdd), JSON.stringify({ data }), getAdminHeaders()).then(res => {
                    this.goBack();
                }).catch(error => {
                    this.setState({
                        loading: false
                    });
                })
            }

        });
    }

    render() {
        return (
            <div className="ctbids-content-main">
                {
                    this.state.pageLoading ?
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                            <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                        </div> : <div>
                            <div id="backbutton" style={{ borderBottom: "1px solid #b6b6b6", padding: "1%" }}>
                                <span className="clickable" onClick={this.goBack}>
                                    <i className=" fa fa-chevron-circle-left fa-lg text-back rounded-circle"></i>
                                    <span style={{ paddingLeft: "1em" }}>Back</span>
                                </span>
                                <span style={{ marginLeft: "0.5em", paddingLeft: "1em", borderLeft: "1px solid", color: "#A12142" }}>{this.edit ? 'Content Edit' : 'Ctbids Content'}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-center">
                                <div className='large-screen-width'>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <div className="ctbids-content">
                                            <Form>
                                                <div className="float-control">
                                                    <div className="text">
                                                        {
                                                            this.state.formControls.type.error && this.state.formControls.type.error.length ? <small className="error">{this.state.formControls.type.error}</small> : null
                                                        }
                                                    </div>
                                                    <Form.Control as="select" id="type" name="type" value={this.state.formControls.type.value} className={this.state.formControls.type.value ? 'form-control-active' : ''} placeholder=" " required
                                                        onChange={(event) => this.onInputValidation(event)} disabled={this.edit}>
                                                        <option disabled={true} value={""} selected>Select type</option>
                                                        {this.contentType.map((type, index) =>
                                                            <option key={index} value={type.value}>{type.name}</option>
                                                        )}
                                                    </Form.Control>
                                                    <Form.Label htmlFor="locationid" className="required">Type</Form.Label>
                                                </div>
                                                <div>
                                                    <ReactQuill theme="snow" modules={this.modules} value={this.state.formControls.description.value} onChange={this.templateChange} ref={(el) => this.textEditorRef = el} formats={this.formats} />
                                                    <div className="text">
                                                        {
                                                            this.state.formControls.description.error && this.state.formControls.description.error.length ? <small className="text-danger">{this.state.formControls.description.error}</small> : null
                                                        }
                                                    </div>
                                                </div>
                                                {
                                                    this.edit ?
                                                        <div className="float-control">
                                                            <div className="text">
                                                                {
                                                                    this.state.formControls.status.error && this.state.formControls.status.error.length ? <small className="error">{this.state.formControls.status.error}</small> : null
                                                                }
                                                            </div>
                                                            <Form.Control as="select" id="status" name="status" value={this.state.formControls.status.value} className={this.state.formControls.status.value ? 'form-control-active' : ''} placeholder=" " required
                                                                onChange={(event) => this.onInputValidation(event)}>
                                                                <option disabled={true} value={""} selected>Select status</option>
                                                                {this.status.map((status, index) =>
                                                                    <option key={index} value={status.value}>{status.name}</option>
                                                                )}
                                                            </Form.Control>
                                                            <Form.Label htmlFor="locationid" className="required">Type</Form.Label>
                                                        </div>
                                                        : null
                                                }
                                                <div className="buttonGroup mt-3">
                                                    <div className="d-flex align-items-center justify-content-center">
                                                        <Button variant="light" className="mx-1 cencel-button" onClick={this.goBack}>Cancel</Button>
                                                        {
                                                            this.state.loading ?
                                                                <Button variant="secondary" className="mx-1 d-flex" disabled={true} type="button">
                                                                    Submit
                                                                    <Spinner className="mx-1" animation="border" size="sm" role="status" aria-hidden="true" as="span"></Spinner>
                                                                </Button>
                                                                :
                                                                <Button variant="secondary" className="mx-1" disabled={!this.state.formValid} onClick={this.submit}>Submit</Button>
                                                        }
                                                    </div>
                                                </div>
                                            </Form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                }
            </div>
        );
    }
}

export default connect(state => state)(CtbidsContentEdit);